import { Basename } from '@/types/Basename';

import {
    type BrowseDimensionPair,
    type BrowsePathBase,
    buildBrowseCategoryPath,
    isKnownBrowseDimensionKey,
} from './browsePath';
import { getTopCategoryIdFromSlug } from './resolveCategorySlug';

const BROWSE_ROOTS = new Set(['c', 'price-guide', 'similar-items']);

/** Single segment allowed before `/c|price-guide|similar-items` (SSR pathname includes this; RR basename strips it client-side). */
const ALLOWED_LOCALE_BEFORE_BROWSE_ROOT = new Set([Basename.ENGB.slice(1)]);

/**
 * Indexed `/c/:topSlug/.../:digit/:digit/` URLs (pre browse-in-path SEO) sometimes carried facet labels ahead of two
 * numeric taxonomy ids. Canonical browse paths omit those ids.
 *
 * Shapes handled:
 * - `/c/top/value/d1/d2` — lone `value` slug before ids → migrated to **`/top/style/value/`** on safe tops.
 * - `/c/top/dimension/value/d1/d2` — explicit dimension key → **`/top/dimension/value/`**
 *
 * Combination-only L1s (art creators, jewelry sub-brands, etc.) opt out of automatic `style` inference.
 */
const TOP_SLUGS_SKIP_AUTOMATIC_STYLE_INFERENCE: ReadonlySet<string> = new Set(
    ['art', 'jewelry', 'fashion', 'watches', 'coins', 'coins-currency-and-stamps'].map((slug) => slug.toLowerCase())
);

const browseRootToBase = (root: string): BrowsePathBase => {
    if (root === 'price-guide') {
        return '/price-guide';
    }
    if (root === 'similar-items') {
        return '/similar-items';
    }
    return '/c';
};

/**
 * Splits a browse-ish URL into its (optional) locale prefix + scoped segments under `/c|price-guide|similar-items`.
 * Returns `null` when the path has no browse root or has more than one leading segment that isn't an allowed locale.
 */
const getBrowseSegments = (pathname: string): { leadingPrefix: string; scoped: string[] } | null => {
    if (typeof pathname !== 'string' || pathname === '') {
        return null;
    }
    const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname : `${pathname}/`;
    const segments = normalized.split('/').filter(Boolean);
    const rootIndex = segments.findIndex((seg) => BROWSE_ROOTS.has(seg));
    if (rootIndex === -1) {
        return null;
    }
    const leadingSegments = segments.slice(0, rootIndex);
    if (leadingSegments.length > 1) {
        return null;
    }
    if (leadingSegments.length === 1 && !ALLOWED_LOCALE_BEFORE_BROWSE_ROOT.has(leadingSegments[0].toLowerCase())) {
        return null;
    }
    return {
        leadingPrefix: leadingSegments.length === 1 ? `/${leadingSegments[0]}` : '',
        scoped: segments.slice(rootIndex),
    };
};

/**
 * Prefix segment before `/c|price-guide|similar-items`, for rebuilding redirects when category-api omits locale.
 */
export const getLeadingBrowseLocalePrefix = (pathname: string): string => {
    const parsed = getBrowseSegments(pathname);
    return parsed ? parsed.leadingPrefix : '';
};

/**
 * If `pathname` matches a legacy faceted quad/triple shape with trailing numeric IDs, returns the canonical browse
 * path without those IDs. Otherwise `null`.
 */
export const getLegacyQuadFacetedCanonicalPath = (pathname: string): string | null => {
    const parsed = getBrowseSegments(pathname);
    if (!parsed) {
        return null;
    }
    const { leadingPrefix, scoped } = parsed;
    const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname : `${pathname}/`;

    const root = scoped[0];

    if (scoped.length < 5) {
        return null;
    }

    const lastSegment = scoped[scoped.length - 1];
    const prevSegment = scoped[scoped.length - 2];
    if (!/^\d+$/.test(lastSegment) || !/^\d+$/.test(prevSegment)) {
        return null;
    }

    /** Category slug + facet segments between browse root and the two trailing numeric ids */
    const betweenRootAndDigits = scoped.slice(1, -2);
    if (betweenRootAndDigits.length < 2 || betweenRootAndDigits.length > 3) {
        return null;
    }

    const categorySlug = betweenRootAndDigits[0];
    let pairs: BrowseDimensionPair[];

    if (betweenRootAndDigits.length === 2) {
        const middleSlug = betweenRootAndDigits[1];
        const middleKey = middleSlug.toLowerCase();
        if (isKnownBrowseDimensionKey(middleKey)) {
            /* Need dimension + value triple shape; ambiguous two-segment path */
            return null;
        }
        if (TOP_SLUGS_SKIP_AUTOMATIC_STYLE_INFERENCE.has(categorySlug.toLowerCase())) {
            return null;
        }
        pairs = [{ dimensionKey: 'style', valueSlug: middleSlug }];
    } else {
        const [, dimensionSegment, valueSegment] = betweenRootAndDigits;
        const dimKeyLower = dimensionSegment.toLowerCase();
        if (!isKnownBrowseDimensionKey(dimKeyLower)) {
            return null;
        }
        pairs = [{ dimensionKey: dimKeyLower, valueSlug: valueSegment }];
    }

    const base = browseRootToBase(root);
    const built = buildBrowseCategoryPath(categorySlug, pairs, { base });
    const relocatedBuilt = `${leadingPrefix}${built}`;
    const normalizedComparable = normalized;
    const builtComparable = relocatedBuilt.endsWith('/') ? relocatedBuilt : `${relocatedBuilt}/`;

    if (builtComparable === normalizedComparable) {
        return null;
    }

    return relocatedBuilt.endsWith('/') ? relocatedBuilt : `${relocatedBuilt}/`;
};

/**
 * Detects `redirectPath` values from `resolve-browse-url` that point to the slug-only path of an unknown
 * sub-category (e.g. `/c/earrings/`). Following such a redirect would just bounce back to the same broken
 * state because the destination has no top-cat id either, producing the loop / Oops the user reported.
 */
export const isUnknownSlugOnlyBrowsePath = (redirectPath: string): boolean => {
    const parsed = getBrowseSegments(redirectPath);
    if (!parsed) {
        return false;
    }
    /* Slug-only path is exactly two segments after the optional locale: [browseRoot, slug]. */
    if (parsed.scoped.length !== 2) {
        return false;
    }
    return getTopCategoryIdFromSlug(parsed.scoped[1]) === null;
};

/**
 * True when `redirectPath` is the slug-only browse path for the same top-cat that `pathname` lives under,
 * AND `pathname` carries additional segments (facet value, numeric ids, etc.). Following such a redirect
 * strips meaningful context and bounces the user to a generic top-cat page — e.g.
 * `/c/art/lithograph/1/25811/` -> `/c/art/`. The loader can usually render something useful from the
 * extra context (known top-cat id + post-search facet validation), so suppress these specific redirects.
 */
export const redirectsToSameTopCatSlugOnly = (pathname: string, redirectPath: string): boolean => {
    const fromParsed = getBrowseSegments(pathname);
    const toParsed = getBrowseSegments(redirectPath);
    if (!fromParsed || !toParsed) {
        return false;
    }
    /* Original must be more than just [browseRoot, slug]; otherwise there is nothing being stripped. */
    if (fromParsed.scoped.length <= 2) {
        return false;
    }
    /* Destination must be exactly [browseRoot, slug]. */
    if (toParsed.scoped.length !== 2) {
        return false;
    }
    /* Same browse root (c/price-guide/similar-items) AND same top-cat slug. */
    return (
        fromParsed.scoped[0] === toParsed.scoped[0] &&
        fromParsed.scoped[1].toLowerCase() === toParsed.scoped[1].toLowerCase()
    );
};

/**
 * True iff `pathname` looks like the legacy faceted-numeric-tail browse shape:
 * `/c/{slug}/(value|dim/value)/{d1}/{d2}/` (with an optional /en-gb locale prefix). These are the legacy
 * SEO URLs we can't always auto-canonicalize (combo-only L1s opt out of `style` inference), so when the
 * resolver can't clarify them either, the loader should render with whatever context we have rather than
 * dump back to slug-only.
 */
export const isLegacyFacetedNumericTailBrowsePath = (pathname: string): boolean => {
    const parsed = getBrowseSegments(pathname);
    if (!parsed) {
        return false;
    }
    const { scoped } = parsed;
    if (scoped.length < 5) {
        return false;
    }
    const last = scoped[scoped.length - 1];
    const prev = scoped[scoped.length - 2];
    return /^\d+$/.test(last) && /^\d+$/.test(prev);
};

/**
 * Combines {@link redirectsToSameTopCatSlugOnly} and {@link isLegacyFacetedNumericTailBrowsePath} for
 * the common guard: suppress API/category redirects that strip a legacy faceted-numeric-tail URL back
 * to its own top-cat slug-only path. Non-legacy malformed URLs (e.g. tilde multi-value, invalid creator
 * values) still get the resolver's slug-only short-circuit; this guard only protects the legacy SEO
 * shapes where the trailing numeric ids carry context the loader can still surface.
 */
export const stripsLegacyNumericTailToOwnTopSlug = (pathname: string, redirectPath: string): boolean =>
    isLegacyFacetedNumericTailBrowsePath(pathname) && redirectsToSameTopCatSlugOnly(pathname, redirectPath);
